import React, { useMemo, useState } from 'react';
import {
  Dimmer,
  Icon,
  Label,
  List,
  Loader,
  Message,
  Modal,
  Checkbox,
} from 'semantic-ui-react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import Button from '../../../../components/Button';
import {
  dangerNotification,
  successNotification,
} from '../../../../services/notification';
import { transmitBatch } from '../../../../services/fire-insurance-batches';
import {
  getBatchStatusColor,
  translateBatchStatus,
} from '../types';
import { canTransmitBatch } from '../utils/batch-transmit.util';

export interface TransmitBatchConfirmModalBatch {
  id: string;
  batchNumber: number;
  status: string;
  itemCount: number;
  hasSingleItem: boolean;
  isManualProcessing: boolean;
  lastTransmittedAt?: string | null;
  lastTransmittedCompetence?: string | null;
  estate: {
    name: string;
  };
}

interface TransmitBatchConfirmModalProps {
  open: boolean;
  batch: TransmitBatchConfirmModalBatch | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ContextBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 16px;
`;

const ContextItem = styled.span`
  color: #444;
  font-size: 13px;

  strong {
    color: #333;
  }
`;

const IntroText = styled.p`
  color: #444;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 12px;
`;

const ImplicationsList = styled(List)`
  &.ui.list {
    margin: 0 0 16px;
    color: #444;
    font-size: 13px;
    line-height: 1.5;
  }
`;

const TransmitBatchConfirmModal: React.FC<TransmitBatchConfirmModalProps> = ({
  open,
  batch,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [force, setForce] = useState(false);

  const eligibility = useMemo(
    () =>
      batch
        ? canTransmitBatch({
            status: batch.status,
            isManualProcessing: batch.isManualProcessing,
            itemCount: batch.itemCount,
          })
        : { allowed: false },
    [batch],
  );
  const alreadyTransmittedThisMonth = Boolean(
    batch?.lastTransmittedCompetence &&
      moment(batch.lastTransmittedCompetence).isSame(moment(), 'month'),
  );

  const handleClose = () => {
    if (loading) {
      return;
    }
    setForce(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (!batch || !eligibility.allowed) {
      return;
    }

    setLoading(true);
    try {
      await transmitBatch(batch.id, force);
      successNotification(
        'Sucesso',
        'Transmissão do lote agendada com sucesso. O status será atualizado em instantes.',
      );
      onSuccess();
      setForce(false);
      onClose();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : (error as { message?: string })?.message ||
            'Erro ao agendar transmissão do lote';
      dangerNotification('Erro', message);
    } finally {
      setLoading(false);
    }
  };

  if (!batch) {
    return null;
  }

  return (
    <Modal open={open} onClose={handleClose} size="small" closeOnDimmerClick={!loading}>
      <Modal.Header>
        <Icon name="paper plane" />
        Transmitir lote
      </Modal.Header>

      <Modal.Content>
        <Dimmer active={loading} inverted>
          <Loader indeterminate>Agendando transmissão...</Loader>
        </Dimmer>

        <ContextBanner>
          <ContextItem>
            <strong>Lote #{batch.batchNumber}</strong>
          </ContextItem>
          <ContextItem>·</ContextItem>
          <ContextItem>{batch.estate.name}</ContextItem>
          <ContextItem>·</ContextItem>
          <Label color={getBatchStatusColor(batch.status) as any} size="tiny">
            {translateBatchStatus(batch.status)}
          </Label>
        </ContextBanner>

        <IntroText>
          Você está prestes a disparar manualmente o processamento de faturamento e
          transmissão deste lote na Yelum — o mesmo fluxo executado pelo job automático
          de fechamento.
        </IntroText>

        <ImplicationsList bulleted>
          <List.Item>
            O lote será colocado em processamento e o sistema consultará a Yelum.
          </List.Item>
          <List.Item>
            Dependendo do estado na seguradora, pode criar proposta, aguardar emissão da
            apólice ou seguir com a abertura de faturas.
          </List.Item>
          <List.Item>
            A operação é assíncrona: o job roda em fila com até 3 tentativas, com
            intervalo de 30 minutos entre elas em caso de falha.
          </List.Item>
          <List.Item>
            Se o lote ficar com status Erro por um motivo recuperável (ex.: timeout), um
            job diário tenta transmiti-lo automaticamente às 8h — não é necessário
            repetir a transmissão manualmente todos os dias.
          </List.Item>
          <List.Item>
            O status do lote será atualizado automaticamente conforme o processamento
            avançar.
          </List.Item>
        </ImplicationsList>

        {!eligibility.allowed && eligibility.reason ? (
          <Message negative icon size="small">
            <Icon name="warning circle" />
            <Message.Content>{eligibility.reason}</Message.Content>
          </Message>
        ) : null}

        {eligibility.allowed && alreadyTransmittedThisMonth ? (
          <Message warning icon size="small">
            <Icon name="warning sign" />
            <Message.Content>
              <Message.Header>
                Esta competência já foi transmitida
              </Message.Header>
              {batch.lastTransmittedAt
                ? `Registrada em ${moment(batch.lastTransmittedAt).format(
                    'DD/MM/YYYY [às] HH:mm',
                  )}.`
                : 'O lote já possui uma transmissão registrada neste mês.'}{' '}
              Só confirme novamente se novos itens foram incluídos e você realmente
              precisa retransmitir o lote.
              <div style={{ marginTop: 8 }}>
                <Checkbox
                  checked={force}
                  onChange={(_, data) => setForce(Boolean(data.checked))}
                  label="Confirmo que desejo retransmitir a competência"
                />
              </div>
            </Message.Content>
          </Message>
        ) : null}

        {eligibility.allowed && batch.hasSingleItem ? (
          <Message warning icon size="small">
            <Icon name="warning sign" />
            <Message.Content>
              Este lote possui apenas 1 item. A Yelum recomenda no mínimo 2 itens para
              transmissão.
            </Message.Content>
          </Message>
        ) : null}

        {eligibility.allowed ? (
          <Message info size="small" style={{ marginBottom: 0 }}>
            <Icon name="info circle" />
            Tem certeza de que deseja continuar?
          </Message>
        ) : null}
      </Modal.Content>

      <Modal.Actions>
        <Button basic onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          color="purple"
          onClick={handleConfirm}
          loading={loading}
          disabled={loading || !eligibility.allowed || (alreadyTransmittedThisMonth && !force)}
        >
          Confirmar transmissão
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default TransmitBatchConfirmModal;
